import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';

interface Item { label: string; sub?: string; to: string; kind: 'page' | 'client' | 'vendor'; }

const PAGES: Item[] = [
  { label: 'Home', to: '/', kind: 'page' },
  { label: 'Command Dashboard', to: '/dashboard', kind: 'page' },
  { label: 'Clients', to: '/clients', kind: 'page' },
  { label: 'Vendors', to: '/vendors', kind: 'page' },
  { label: 'Customer CRM', to: '/customers', kind: 'page' },
  { label: 'Materials & Dies', to: '/catalog', kind: 'page' },
  { label: 'Intake & Requests', to: '/intake', kind: 'page' },
  { label: 'Prepress Workspace', to: '/prepress', kind: 'page' },
  { label: 'Task Board', to: '/tasks', kind: 'page' },
  { label: 'Shared Calendar', to: '/calendar', kind: 'page' },
  { label: 'Web Leads', to: '/web-leads', kind: 'page' },
  { label: 'Opportunity Pipeline', to: '/pipeline', kind: 'page' },
  { label: 'Quote Builder', to: '/quote-builder', kind: 'page' },
  { label: 'Quote Engine', to: '/quote-engine', kind: 'page' },
  { label: 'Quote Registry', to: '/quotes', kind: 'page' },
  { label: 'Follow-Ups', to: '/follow-ups', kind: 'page' },
  { label: 'Reorder Intelligence', to: '/reorders', kind: 'page' },
  { label: 'Communication Log', to: '/comms-log', kind: 'page' },
  { label: 'Sales Calendar', to: '/sales-calendar', kind: 'page' },
  { label: 'AI Sales Assistant', to: '/sales-ai', kind: 'page' },
  { label: 'AI Command Room', to: '/ai', kind: 'page' },
  { label: 'Operating Manual', to: '/manual', kind: 'page' },
  { label: 'KPI Dashboard', to: '/kpis', kind: 'page' },
  { label: 'Implementation Roadmap', to: '/roadmap', kind: 'page' },
];

export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { demo } = useAuth();
  const { data } = useAccounts(demo);
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const accountItems = useMemo<Item[]>(() => (data?.accounts ?? []).map((a) => ({
    label: a.name,
    sub: [a.type === 'vendor' ? 'Vendor' : 'Client', a.industry, [a.billing?.city, a.billing?.state].filter(Boolean).join(', ')].filter(Boolean).join(' · '),
    to: a.type === 'vendor' ? `/vendors?open=${encodeURIComponent(a.id)}` : `/client?id=${encodeURIComponent(a.id)}`,
    kind: a.type === 'vendor' ? 'vendor' : 'client',
  })), [data]);

  const results = useMemo<Item[]>(() => {
    const t = q.trim().toLowerCase();
    if (!t) return PAGES.slice(0, 8);
    const pageHits = PAGES.filter((p) => p.label.toLowerCase().includes(t));
    const acctHits = accountItems.filter((a) => a.label.toLowerCase().includes(t) || (a.sub || '').toLowerCase().includes(t)).slice(0, 12);
    return [...pageHits, ...acctHits].slice(0, 14);
  }, [q, accountItems]);

  useEffect(() => { if (open) { setQ(''); setSel(0); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);
  useEffect(() => { setSel(0); }, [q]);

  if (!open) return null;

  const go = (it?: Item) => { if (!it) return; onClose(); nav(it.to); };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[sel]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  const icon = (k: Item['kind']) => (k === 'client' ? '◎' : k === 'vendor' ? '◐' : '→');

  return (
    <div className="palette-scrim" onClick={onClose}>
      <div className="palette" onClick={(e) => e.stopPropagation()}>
        <div className="palette-search">
          <span className="palette-ico">⌕</span>
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search clients, vendors, contacts, or jump to a page…" />
          <kbd className="palette-kbd">esc</kbd>
        </div>
        <div className="palette-list">
          {results.length === 0 && <div className="palette-empty">No matches for “{q}”.</div>}
          {results.map((it, i) => (
            <button key={it.to + i} className={`palette-item${i === sel ? ' sel' : ''}`} onMouseEnter={() => setSel(i)} onClick={() => go(it)}>
              <span className={`palette-item-ico k-${it.kind}`}>{icon(it.kind)}</span>
              <span className="palette-item-main"><strong>{it.label}</strong>{it.sub && <span className="palette-item-sub">{it.sub}</span>}</span>
              <span className="palette-item-kind">{it.kind}</span>
            </button>
          ))}
        </div>
        <div className="palette-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>⌘</kbd><kbd>K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}
